import React, { useState, useEffect, useCallback } from 'react'
import { 
  Input, 
  Card, 
  List, 
  Pagination, 
  Empty, 
  Spin, 
  Tag, 
  Typography, 
  Space, 
  Button,
  Drawer,
  Form,
  Select,
  InputNumber,
  Divider,
  AutoComplete
} from 'antd'
import { 
  SearchOutlined, 
  FilterOutlined, 
  BookOutlined, 
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useDebounce } from 'ahooks'
import { apiRequest } from '../../utils/api'
import './SearchComponent.css'

const { Search } = Input
const { Text, Title } = Typography
const { Option } = Select

function SearchComponent() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  
  // 搜索状态
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '')
  const [searchResults, setSearchResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  
  // 高级搜索
  const [advancedVisible, setAdvancedVisible] = useState(false)
  const [advancedForm] = Form.useForm()
  
  // 搜索建议
  const [suggestions, setSuggestions] = useState([])
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  
  // 防抖搜索查询
  const debouncedQuery = useDebounce(searchQuery, { wait: 500 })
  
  // 初始化时如果有查询参数则执行搜索
  useEffect(() => {
    const query = searchParams.get('q')
    if (query) {
      setSearchQuery(query)
      performSearch(query, {
        page: parseInt(searchParams.get('page') || '1'),
        category: searchParams.get('category'),
        author: searchParams.get('author'),
        dynasty: searchParams.get('dynasty')
      })
    }
  }, [])

  // 执行搜索
  const performSearch = useCallback(async (query, options = {}) => {
    if (!query?.trim()) {
      setSearchResults([])
      setPagination(prev => ({ ...prev, total: 0 }))
      return
    }

    setLoading(true)
    
    try {
      const params = new URLSearchParams({
        q: query.trim(),
        limit: options.pageSize || pagination.pageSize,
        offset: ((options.page || pagination.current) - 1) * (options.pageSize || pagination.pageSize)
      })

      // 添加筛选条件
      if (options.category) params.append('category', options.category)
      if (options.author) params.append('author', options.author)
      if (options.dynasty) params.append('dynasty', options.dynasty)
      if (options.sort) params.append('sort', options.sort)

      const response = await apiRequest(`/search?${params}`)
      const data = await response.json()

      if (response.ok) {
        setSearchResults(data.results || [])
        setPagination(prev => ({
          ...prev,
          current: options.page || prev.current,
          total: data.pagination?.total || 0
        }))
        
        // 更新URL参数
        const newParams = new URLSearchParams(searchParams)
        newParams.set('q', query)
        if (options.page && options.page !== 1) {
          newParams.set('page', options.page.toString())
        } else {
          newParams.delete('page')
        }
        if (options.category) newParams.set('category', options.category)
        if (options.author) newParams.set('author', options.author)
        if (options.dynasty) newParams.set('dynasty', options.dynasty)
        
        setSearchParams(newParams)
      } else {
        console.error('搜索失败:', data.error)
        setSearchResults([])
      }
    } catch (error) {
      console.error('搜索请求失败:', error)
      setSearchResults([])
    } finally {
      setLoading(false)
    }
  }, [pagination.pageSize, pagination.current, searchParams, setSearchParams])

  // 获取搜索建议
  const getSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([])
      return
    }

    setSuggestionsLoading(true)
    try {
      const response = await apiRequest(`/search/suggestions?q=${encodeURIComponent(query)}`)
      const data = await response.json()
      
      if (response.ok) {
        setSuggestions(data.suggestions || [])
      }
    } catch (error) {
      console.error('获取搜索建议失败:', error)
    } finally {
      setSuggestionsLoading(false)
    }
  }, [])

  // 处理搜索输入变化
  useEffect(() => {
    if (debouncedQuery !== searchParams.get('q')) {
      getSuggestions(debouncedQuery)
    }
  }, [debouncedQuery, getSuggestions, searchParams])

  // 处理搜索提交
  const handleSearch = (value, options = {}) => {
    const query = value?.trim()
    if (query) {
      performSearch(query, { ...options, page: 1 })
    }
  }

  // 处理分页变化
  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({ ...prev, current: page, pageSize }))
    performSearch(searchQuery, { 
      page, 
      pageSize,
      category: searchParams.get('category'),
      author: searchParams.get('author'),
      dynasty: searchParams.get('dynasty')
    })
  }

  // 处理高级搜索
  const handleAdvancedSearch = (values) => {
    const { content, title, author, dynasty, category, minLength, maxLength } = values
    
    // 构建搜索查询
    let query = content || ''
    const options = {
      page: 1,
      author: author || searchParams.get('author'),
      dynasty: dynasty || searchParams.get('dynasty'),
      category: category || searchParams.get('category')
    }

    if (title) {
      query = title
    }

    if (query.trim()) {
      performSearch(query, options)
      setAdvancedVisible(false)
    }
  }

  // 清除所有筛选
  const clearFilters = () => {
    const newParams = new URLSearchParams()
    if (searchQuery) {
      newParams.set('q', searchQuery)
    }
    setSearchParams(newParams)
    
    if (searchQuery) {
      performSearch(searchQuery, { page: 1 })
    }
  }

  // 跳转到书籍详情
  const goToBook = (bookId) => {
    navigate(`/books/${bookId}`)
  }

  // 准备搜索建议选项
  const suggestionOptions = suggestions.map(suggestion => ({
    value: suggestion.suggestion,
    label: (
      <div className="suggestion-item">
        <span className="suggestion-text">{suggestion.suggestion}</span>
        <Tag size="small" color={suggestion.type === 'book' ? 'blue' : 'green'}>
          {suggestion.type === 'book' ? '书名' : '作者'}
        </Tag>
      </div>
    )
  }))

  return (
    <div className="search-page">
      <div className="search-container">
        {/* 搜索区域 */}
        <div className="search-section">
          <Title level={2} className="search-title">
            <SearchOutlined /> 古籍全文搜索
          </Title>
          
          <div className="search-box">
            <AutoComplete
              className="search-input"
              options={suggestionOptions}
              value={searchQuery}
              onChange={setSearchQuery}
              onSelect={(value) => {
                setSearchQuery(value)
                handleSearch(value)
              }}
              notFoundContent={suggestionsLoading ? <Spin size="small" /> : null}
            >
              <Search
                placeholder="请输入要搜索的内容..."
                allowClear
                enterButton={
                  <Button type="primary" icon={<SearchOutlined />}>
                    搜索
                  </Button>
                }
                size="large"
                onSearch={handleSearch}
              />
            </AutoComplete>
            
            <Button
              type="text"
              icon={<FilterOutlined />}
              onClick={() => setAdvancedVisible(true)}
              className="advanced-search-btn"
            >
              高级搜索
            </Button>
          </div>

          {/* 当前筛选条件 */}
          {(searchParams.get('category') || searchParams.get('author') || searchParams.get('dynasty')) && (
            <div className="current-filters">
              <Text type="secondary">当前筛选: </Text>
              <Space wrap>
                {searchParams.get('category') && (
                  <Tag closable onClose={() => {
                    const newParams = new URLSearchParams(searchParams)
                    newParams.delete('category')
                    setSearchParams(newParams)
                  }}>
                    分类: {searchParams.get('category')}
                  </Tag>
                )}
                {searchParams.get('author') && (
                  <Tag closable onClose={() => {
                    const newParams = new URLSearchParams(searchParams)
                    newParams.delete('author')
                    setSearchParams(newParams)
                  }}>
                    作者: {searchParams.get('author')}
                  </Tag>
                )}
                {searchParams.get('dynasty') && (
                  <Tag closable onClose={() => {
                    const newParams = new URLSearchParams(searchParams)
                    newParams.delete('dynasty')
                    setSearchParams(newParams)
                  }}>
                    朝代: {searchParams.get('dynasty')}
                  </Tag>
                )}
                <Button type="link" size="small" onClick={clearFilters}>
                  清除全部
                </Button>
              </Space>
            </div>
          )}
        </div>

        {/* 搜索结果 */}
        <div className="search-results">
          {searchQuery && (
            <div className="results-header">
              <Title level={4}>
                搜索结果
                {!loading && pagination.total > 0 && (
                  <Text type="secondary" className="results-count">
                    (共找到 {pagination.total} 个结果)
                  </Text>
                )}
              </Title>
            </div>
          )}

          <Spin spinning={loading}>
            {searchResults.length > 0 ? (
              <>
                <List
                  className="results-list"
                  dataSource={searchResults}
                  renderItem={(item) => (
                    <List.Item className="result-item">
                      <Card 
                        hoverable
                        className="result-card"
                        onClick={() => goToBook(item.id)}
                      >
                        <div className="result-header">
                          <Title level={5} className="result-title">
                            <BookOutlined /> {item.title}
                          </Title>
                          <div className="result-meta">
                            {item.author && (
                              <Tag icon={<UserOutlined />} color="blue">
                                {item.author}
                              </Tag>
                            )}
                            {item.dynasty && (
                              <Tag icon={<CalendarOutlined />} color="green">
                                {item.dynasty}
                              </Tag>
                            )}
                            {item.category_name && (
                              <Tag color="orange">
                                {item.category_name}
                              </Tag>
                            )}
                          </div>
                        </div>
                        
                        {item.snippet && (
                          <div 
                            className="result-snippet"
                            dangerouslySetInnerHTML={{ __html: item.snippet }}
                          />
                        )}
                        
                        <div className="result-footer">
                          <Space>
                            <Text type="secondary">
                              <FileTextOutlined /> {item.page_count} 页
                            </Text>
                            <Text type="secondary">
                              {item.character_count?.toLocaleString()} 字
                            </Text>
                          </Space>
                        </div>
                      </Card>
                    </List.Item>
                  )}
                />

                <div className="pagination-container">
                  <Pagination
                    current={pagination.current}
                    total={pagination.total}
                    pageSize={pagination.pageSize}
                    showSizeChanger
                    showQuickJumper
                    showTotal={(total, range) => 
                      `第 ${range[0]}-${range[1]} 条，共 ${total} 条结果`
                    }
                    onChange={handlePageChange}
                    onShowSizeChange={handlePageChange}
                  />
                </div>
              </>
            ) : searchQuery && !loading ? (
              <Empty
                description="没有找到相关结果"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="primary" onClick={() => setAdvancedVisible(true)}>
                  尝试高级搜索
                </Button>
              </Empty>
            ) : !searchQuery ? (
              <Empty
                description="请输入搜索关键词"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : null}
          </Spin>
        </div>
      </div>

      {/* 高级搜索抽屉 */}
      <Drawer
        title="高级搜索"
        placement="right"
        width={400}
        onClose={() => setAdvancedVisible(false)}
        open={advancedVisible}
      >
        <Form
          form={advancedForm}
          layout="vertical"
          onFinish={handleAdvancedSearch}
        >
          <Form.Item name="title" label="书名">
            <Input placeholder="请输入书名" />
          </Form.Item>
          
          <Form.Item name="author" label="作者">
            <Input placeholder="请输入作者" />
          </Form.Item>
          
          <Form.Item name="content" label="内容">
            <Input.TextArea 
              placeholder="请输入要搜索的内容"
              rows={3}
            />
          </Form.Item>
          
          <Form.Item name="dynasty" label="朝代">
            <Select placeholder="请选择朝代" allowClear>
              <Option value="春秋">春秋</Option>
              <Option value="战国">战国</Option>
              <Option value="秦">秦</Option>
              <Option value="汉">汉</Option>
              <Option value="三国">三国</Option>
              <Option value="晋">晋</Option>
              <Option value="南北朝">南北朝</Option>
              <Option value="隋">隋</Option>
              <Option value="唐">唐</Option>
              <Option value="五代">五代</Option>
              <Option value="宋">宋</Option>
              <Option value="元">元</Option>
              <Option value="明">明</Option>
              <Option value="清">清</Option>
            </Select>
          </Form.Item>
          
          <Form.Item name="category" label="分类">
            <Select placeholder="请选择分类" allowClear>
              <Option value="经部">经部</Option>
              <Option value="史部">史部</Option>
              <Option value="子部">子部</Option>
              <Option value="集部">集部</Option>
            </Select>
          </Form.Item>
          
          <Divider />
          
          <Form.Item label="文本长度">
            <Input.Group compact>
              <Form.Item name="minLength" style={{ width: '45%' }}>
                <InputNumber 
                  placeholder="最少字数" 
                  style={{ width: '100%' }}
                  min={0}
                />
              </Form.Item>
              <span style={{ 
                width: '10%', 
                textAlign: 'center', 
                lineHeight: '32px' 
              }}>
                -
              </span>
              <Form.Item name="maxLength" style={{ width: '45%' }}>
                <InputNumber 
                  placeholder="最多字数" 
                  style={{ width: '100%' }}
                  min={0}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                搜索
              </Button>
              <Button onClick={() => {
                advancedForm.resetFields()
                setAdvancedVisible(false)
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}

export default SearchComponent